import { useEffect, useState } from "react";
import { Loader2, ImageIcon, Sparkles, Mic2, Camera, X, Check, RefreshCw, Download, Library, FolderPlus, Captions, Tv } from "lucide-react";
import { mistApi, pipelinesApi, streamAsset, downloadAsset } from "@/api/mist";
import { profileGalleryApi, streamProfileMedia } from "@/api/profileGallery";
import { setSingerToViz } from "@/lib/directorPrefill";
import { toast } from "sonner";

// Slots: 1 = Singer face portrait (Lip-Sync), 2-5 = B-roll style seeds.
const SLOTS = [0, 1, 2, 3, 4];

function GalleryPicker({ onPick, onClose }) {
  const [imgs, setImgs] = useState(null);
  useEffect(() => { profileGalleryApi.media().then((d) => setImgs(d.images || [])).catch(() => setImgs([])); }, []);
  return (
    <div className="fixed inset-0 z-[200] bg-black/85 flex items-end justify-center" data-testid="ws-gallery-picker" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0d0d0d] border-t-4 border-[#FFE08A] rounded-t-3xl p-4 max-h-[78dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 pb-3 mb-3 border-b-2 border-[#FFE08A]/20">
          <h3 className="flex-1 font-serif text-lg text-white">Pick from your Gallery</h3>
          <button onClick={onClose} data-testid="ws-gallery-close" className="w-8 h-8 grid place-items-center border-2 border-[#FFE08A] text-[#FFE08A]"><X size={15} /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          {imgs == null ? <div className="grid place-items-center py-10"><Loader2 size={20} className="text-[#FFE08A] animate-spin" /></div>
            : imgs.length === 0 ? <p className="font-mono text-[10px] text-white/50 py-8 text-center">No gallery images yet — add some in Profile.</p>
              : <div className="grid grid-cols-3 gap-2">{imgs.map((im) => <GThumb key={im.id} im={im} onPick={onPick} />)}</div>}
        </div>
      </div>
    </div>
  );
}

function GThumb({ im, onPick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => { let ok = true, u; streamProfileMedia(im.id).then((x) => { if (ok) { u = x; setSrc(x); } else URL.revokeObjectURL(x); }).catch(() => {}); return () => { ok = false; if (u) URL.revokeObjectURL(u); }; }, [im.id]);
  return (
    <button onClick={() => onPick(im)} data-testid={`ws-gallery-item-${im.id}`} className="aspect-square overflow-hidden border-2 border-[#1f1f1f] active:translate-y-[1px]">
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center bg-[#111]"><Loader2 size={14} className="animate-spin text-white/30" /></div>}
    </button>
  );
}

// Workspace 2 — the Visual Deep Studio. Sticky track header, one global Vibe
// prompt, a 5-image array (slot 1 = face → lip-sync, 2-5 = b-roll seeds), and
// post-production toggles (on-screen lyrics · MTV lower-thirds).
export default function WorkspaceVisualStudio({ track, onChangeSong }) {
  const [vibe, setVibe] = useState("");
  const [slots, setSlots] = useState([null, null, null, null, null]); // {assetId, preview}
  const [picking, setPicking] = useState(-1);
  const [galleryFor, setGalleryFor] = useState(-1);
  const [lyricsOn, setLyricsOn] = useState(true);
  const [mtvOn, setMtvOn] = useState(false);
  // 3s Retention Intro Card (Audio-Decoupled §1) — a static title card lead-in.
  const [introOpen, setIntroOpen] = useState(false);
  const [introImg, setIntroImg] = useState(null); // {assetId, preview} — user's own 3s card image
  const [introHook, setIntroHook] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(""); const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // {url, assetId}

  const setSlot = (i, val) => setSlots((s) => s.map((x, k) => (k === i ? val : x)));

  const bridge = async (i, fileOrBlob, name, previewUrl) => {
    setPicking(i);
    try {
      const file = fileOrBlob instanceof File ? fileOrBlob : new File([fileOrBlob], name || `slot${i}.png`, { type: fileOrBlob.type || "image/png" });
      const up = await pipelinesApi.uploadSeed(file);
      setSlot(i, { assetId: up.asset_id, preview: previewUrl || URL.createObjectURL(file) });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add that image"); }
    finally { setPicking(-1); }
  };
  const onDevice = async (i, e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) await bridge(i, f, f.name); };
  const onGalleryPick = async (im) => {
    const i = galleryFor; setGalleryFor(-1);
    if (i < 0) return;
    try { const url = await streamProfileMedia(im.id); const blob = await (await fetch(url)).blob(); URL.revokeObjectURL(url); await bridge(i, blob, im.filename, null); }
    catch { toast.error("Couldn't load that gallery image"); }
  };

  const canGo = !!slots[0]?.assetId && !busy;

  const onIntroUpload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    try { const up = await pipelinesApi.uploadSeed(f); setIntroImg({ assetId: up.asset_id, preview: URL.createObjectURL(f) }); toast.success("Intro card image set ✓"); }
    catch (er) { toast.error(er?.response?.data?.detail || "Couldn't add that image"); }
  };

  // Audio-Decoupled handoff: send the finished reel + master track to Music Viz
  // and land straight in the align/styling canvas (skipSetup).
  const goMusicViz = () => {
    if (!result) return;
    setSingerToViz({
      skipSetup: true, video_asset_id: result.assetId,
      song_asset_id: track.assetId, song_title: track.title,
      project_name: track.projectName || track.title,
      project_metadata: {
        introCardEnabled: !!introImg,
        introStaticAssetUrl: introImg?.assetId || "",
        centerHookText: introHook.trim(),
      },
    });
    const p = new URLSearchParams(window.location.search);
    p.set("studio", "directormode"); p.set("dstab", "viz");
    window.history.pushState({}, "", `${window.location.pathname}?${p.toString()}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const generate = async () => {
    if (!canGo) return;
    setBusy(true); setResult(null); setStage("Starting…"); setProgress(2);
    try {
      const payload = {
        music_asset_id: track.assetId, seconds: track.length, broll_style: "ai",
        character_asset_id: slots[0].assetId, character_prompt: vibe.trim(), vibe_prompt: vibe.trim(),
        broll_seed_asset_ids: slots.slice(1).filter(Boolean).map((s) => s.assetId),
        burn_lyrics: lyricsOn, mtv_lowerthirds: mtvOn,
        ...(introImg ? { intro_card: { enabled: true, asset_id: introImg.assetId, hook_text: introHook.trim(), seconds: 3 } } : {}),
      };
      const r = await mistApi.singingCharacter(payload);
      const job = await pipelinesApi.poll(r.job_id, { interval: 3000, timeout: 900000,
        onTick: (j) => { if (j?.stage) setStage(String(j.stage)); if (typeof j?.progress === "number") setProgress(j.progress); } });
      if (job.status !== "completed" || !job.asset_id) throw new Error(job.error || "Generation failed");
      let url = null;
      for (let i = 0; i < 4; i++) { try { url = await streamAsset(job.asset_id); break; } catch (e) { if (i === 3) throw e; await new Promise((res) => setTimeout(res, 1500 * (i + 1))); } }
      setResult({ url, assetId: job.asset_id });
      toast.success("Your singing reel is ready ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Generation failed"); }
    finally { setBusy(false); setStage(""); setProgress(0); }
  };

  if (busy) {
    return (
      <div className="flex min-h-[70vh] w-full flex-col items-center justify-center gap-6 p-8 text-center" data-testid="ws-visual-busy">
        <div className="relative"><div className="absolute inset-0 animate-ping rounded-full bg-[#FFE08A]/20" /><div className="relative grid h-16 w-16 place-items-center rounded-full border-2 border-[#FFE08A] bg-[#FFE08A]/10"><Mic2 size={26} className="text-[#FFE08A]" /></div></div>
        <div className="w-full max-w-sm space-y-2">
          <div className="h-4 w-full overflow-hidden border-2 border-black bg-[#141414] shadow-[3px_3px_0px_#000]"><div className="h-full bg-gradient-to-r from-[#FFE08A] to-[#FFC64B] transition-all duration-700" style={{ width: `${Math.max(4, Math.round(progress))}%` }} data-testid="ws-visual-bar" /></div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-white/70" data-testid="ws-visual-stage">{stage || "Starting…"} · {Math.round(progress)}%</p>
        </div>
        <p className="max-w-xs font-mono text-[10px] leading-relaxed text-white/40">Rendering scenes, lip-syncing &amp; burning overlays — keep this tab open.</p>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="ws-visual-studio">
      {/* Sticky track header */}
      <div className="sticky top-0 z-20 flex items-center gap-2 border-b-2 border-[#1a1a1a] bg-[#0A0A0A]/95 px-4 py-2.5 backdrop-blur" data-testid="ws-visual-header">
        <Mic2 size={14} className="shrink-0 text-[#FFE08A]" />
        <p className="min-w-0 flex-1 truncate font-mono text-[11px] text-white/85">Active Track: <span className="font-black text-white">{track.title}</span> · {track.length}s</p>
        <button onClick={onChangeSong} data-testid="ws-change-song" className="flex shrink-0 items-center gap-1 border-2 border-[#FFE08A]/40 bg-[#FFE08A]/10 px-2.5 py-1 font-mono text-[9px] font-black uppercase tracking-widest text-[#FFE08A]"><RefreshCw size={11} /> Change Song</button>
      </div>

      <div className="mx-auto max-w-lg space-y-5 p-4">
        <div>
          <p className="font-mono text-[10px] font-black uppercase tracking-[0.24em] text-[#FFE08A]">Workspace 2 · Visual Studio</p>
          <h2 className="font-serif text-xl text-white">Direct the look</h2>
        </div>

        {/* Global vibe prompt */}
        <section className="space-y-2">
          <p className="font-mono text-[11px] font-black uppercase tracking-widest text-white/90">Vibe · one global style</p>
          <textarea data-testid="ws-vibe" rows={3} value={vibe} maxLength={400} onChange={(e) => setVibe(e.target.value)}
            placeholder="One prompt for the whole reel — e.g. “Vintage hand-drawn rotoscope outline animation, warm film grain”"
            className="w-full resize-y border-2 border-[#1f1f1f] bg-[#111] p-2 font-mono text-[13px] leading-relaxed text-white outline-none focus:border-[#FFE08A]" />
        </section>

        {/* Image array — slot 1 face, 2-5 b-roll seeds */}
        <section className="space-y-2">
          <p className="font-mono text-[11px] font-black uppercase tracking-widest text-white/90">Images · up to 5</p>
          <div className="grid grid-cols-5 gap-1.5" data-testid="ws-image-array">
            {SLOTS.map((i) => {
              const s = slots[i]; const isFace = i === 0;
              return (
                <div key={i} data-testid={`ws-slot-${i}`} className={`relative aspect-[3/4] overflow-hidden border-2 ${isFace ? "border-[#FFE08A]" : "border-[#1f1f1f]"}`}>
                  {s ? (
                    <><img src={s.preview} alt="" className="h-full w-full object-cover" />
                      <button onClick={() => setSlot(i, null)} data-testid={`ws-slot-clear-${i}`} className="absolute top-0.5 right-0.5 grid h-5 w-5 place-items-center bg-black/70 text-white"><X size={11} /></button></>
                  ) : (
                    <div className="grid h-full w-full grid-rows-2">
                      <label className="grid cursor-pointer place-items-center border-b border-white/10 text-white/50 hover:text-[#FFE08A]" data-testid={`ws-slot-device-${i}`}>
                        {picking === i ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => onDevice(i, e)} />
                      </label>
                      <button onClick={() => setGalleryFor(i)} data-testid={`ws-slot-gallery-${i}`} className="grid place-items-center text-white/50 hover:text-[#FFE08A]"><Library size={12} /></button>
                    </div>
                  )}
                  <span className="absolute bottom-0 inset-x-0 bg-black/70 px-1 py-0.5 text-center font-mono text-[7px] font-black uppercase tracking-wider text-white/70">
                    {isFace ? <><Mic2 size={8} className="inline" /> Face</> : <><Camera size={8} className="inline" /> B-roll</>}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="font-mono text-[9px] leading-relaxed text-white/50">Slot 1 is your <span className="text-[#FFE08A]">Singer’s face</span> (lip-synced). Slots 2-5 are optional <span className="text-[#FFE08A]">B-roll style seeds</span>. Each slot takes a device photo or a Gallery pick.</p>
        </section>

        {/* Post-production docks */}
        <section className="grid grid-cols-2 gap-2" data-testid="ws-toggles">
          <button onClick={() => setLyricsOn((v) => !v)} data-testid="ws-toggle-lyrics"
            className={`flex items-center justify-center gap-2 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest ${lyricsOn ? "bg-[#FFE08A] text-black" : "bg-[#121212] text-white/70"}`}>
            <Captions size={13} /> Lyrics {lyricsOn ? "On" : "Off"}
          </button>
          <button onClick={() => setMtvOn((v) => !v)} data-testid="ws-toggle-mtv"
            className={`flex items-center justify-center gap-2 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest ${mtvOn ? "bg-[#FFE08A] text-black" : "bg-[#121212] text-white/70"}`}>
            <Tv size={13} /> MTV Lower-Thirds {mtvOn ? "On" : "Off"}
          </button>
        </section>

        {/* 3s Retention Intro Card — collapsible */}
        <section className="border-2 border-[#1f1f1f]" data-testid="ws-introcard">
          <button onClick={() => setIntroOpen((v) => !v)} data-testid="ws-introcard-toggle"
            className="flex w-full items-center justify-between gap-2 bg-[#121212] px-3 py-2.5 text-left">
            <span className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-widest text-white/85"><Tv size={13} className="text-[#FFE08A]" /> 3s Retention Intro Card</span>
            <span className={`font-mono text-[9px] font-black ${introImg ? "text-[#43d17a]" : "text-white/40"}`}>{introImg ? "ON" : "OPTIONAL"} · {introOpen ? "−" : "+"}</span>
          </button>
          {introOpen && (
            <div className="space-y-2 border-t-2 border-[#1f1f1f] bg-[#0d0d0d] p-3">
              {!introImg ? (
                <label data-testid="ws-introcard-upload" className="flex cursor-pointer items-center justify-center gap-2 border-2 border-dashed border-[#FFE08A]/40 bg-[#111] py-4 font-mono text-[10px] font-black uppercase tracking-widest text-[#FFE08A]/80 hover:border-[#FFE08A]">
                  <ImageIcon size={14} /> Upload your own card image
                  <input type="file" accept="image/*" className="hidden" onChange={onIntroUpload} />
                </label>
              ) : (
                <div className="flex items-center gap-2 border-2 border-[#43d17a]/50 bg-[#43d17a]/10 p-1.5" data-testid="ws-introcard-chip">
                  <span className="h-10 w-10 shrink-0 overflow-hidden border border-black bg-black"><img src={introImg.preview} alt="" className="h-full w-full object-cover" /></span>
                  <span className="flex-1 font-mono text-[9px] font-black text-white/75">Card image ready · holds 3s, timing shifts +3s</span>
                  <button onClick={() => setIntroImg(null)} data-testid="ws-introcard-remove" className="grid h-6 w-6 shrink-0 place-items-center border-2 border-black bg-white text-red-500"><X size={12} /></button>
                </div>
              )}
              <input value={introHook} onChange={(e) => setIntroHook(e.target.value)} data-testid="ws-introcard-hook" maxLength={80}
                placeholder="Centre hook text (optional) — e.g. “Wait for it…”"
                className="w-full border-2 border-[#1f1f1f] bg-[#111] px-3 py-2 font-mono text-[12px] text-white outline-none focus:border-[#FFE08A]" />
              <p className="font-mono text-[9px] leading-relaxed text-white/45">Optional. Leave empty for business as usual. Upload a card and it holds for the first 3s (with your centred text), then the reel plays — the whole timeline shifts by +3s. Carries to Music Viz for fine-tuning.</p>
            </div>
          )}
        </section>

        <button onClick={generate} disabled={!canGo} data-testid="ws-generate"
          className={`flex w-full items-center justify-center gap-2 border-2 border-black py-3.5 font-mono text-[13px] font-black uppercase tracking-widest ${canGo ? "bg-[#FFE08A] text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none" : "cursor-not-allowed bg-[#1a1a1a] text-white/60"}`}>
          <Sparkles size={15} /> Make singing reel
        </button>
        {!slots[0] && <p className="text-center font-mono text-[9px] text-white/40">Add a singer face (slot 1) to start.</p>}
      </div>

      {galleryFor >= 0 && <GalleryPicker onPick={onGalleryPick} onClose={() => setGalleryFor(-1)} />}
      {result && <ReelReady result={result} title={track.title} onContinue={goMusicViz} onClose={() => setResult(null)} />}
    </div>
  );
}

function ReelReady({ result, title, onContinue, onClose }) {
  const [albums, setAlbums] = useState([]);
  const [albumId, setAlbumId] = useState("");
  const [toLibrary, setToLibrary] = useState(true);
  const [toDevice, setToDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { mistApi.albums().then((d) => setAlbums((d.albums || []).filter((a) => !a.track || a.track === "video"))).catch(() => {}); }, []);
  const save = async () => {
    if (!toLibrary && !toDevice) { toast.error("Pick where to save"); return; }
    setBusy(true); const t = toast.loading("Saving…");
    try {
      const done = [];
      if (toLibrary) { try { await mistApi.commitAsset(result.assetId); } catch { /* live */ } if (albumId) { try { await mistApi.updateAlbum(albumId, { add_asset: result.assetId }); } catch { /* best-effort */ } } done.push("Library"); }
      if (toDevice) { try { await downloadAsset(result.assetId, title || "singing-reel", "mp4"); } catch { /* ignore */ } done.push("Device"); }
      toast.success(`Saved to ${done.join(" + ")} ✓`, { id: t }); onClose?.();
    } catch (e) { toast.error(e?.message || "Save failed", { id: t }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/75 p-3 backdrop-blur-sm sm:items-center" data-testid="ws-ready-modal" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="w-full max-w-sm space-y-3 border-2 border-[#FFE08A]/50 bg-[#0a0a0a] p-4 shadow-[6px_6px_0px_#000]">
        <div className="flex items-center justify-between"><h3 className="font-serif text-base text-white">Your reel is ready</h3><button onClick={() => !busy && onClose?.()} data-testid="ws-ready-close" className="text-white/60"><X size={18} /></button></div>
        {result.url && <video src={result.url} controls autoPlay playsInline className="w-full rounded bg-black" data-testid="ws-ready-video" />}
        <button onClick={() => setToLibrary((v) => !v)} data-testid="ws-save-library" className={`flex w-full items-center gap-3 border-2 px-3 py-2.5 text-left ${toLibrary ? "border-[#FFE08A] bg-[#FFE08A]/10" : "border-white/15 bg-black"}`}><span className={`grid h-5 w-5 place-items-center border-2 ${toLibrary ? "border-[#FFE08A] bg-[#FFE08A]" : "border-white/30"}`}>{toLibrary && <Check size={13} className="text-black" />}</span><Library size={16} className="text-[#FFE08A]" /><span className="flex-1 font-mono text-[12px] text-white">Library album</span></button>
        {toLibrary && (
          <div className="flex items-center gap-2 border-l-2 border-[#FFE08A]/30 pl-3"><select data-testid="ws-album-select" value={albumId} onChange={(e) => setAlbumId(e.target.value)} className="flex-1 border-2 border-white/15 bg-black px-3 py-2 font-mono text-[12px] text-white outline-none"><option value="">Unsorted (Reels)</option>{albums.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select><FolderPlus size={14} className="text-white/30" /></div>
        )}
        <button onClick={() => setToDevice((v) => !v)} data-testid="ws-save-device" className={`flex w-full items-center gap-3 border-2 px-3 py-2.5 text-left ${toDevice ? "border-[#FFD028] bg-[#FFD028]/10" : "border-white/15 bg-black"}`}><span className={`grid h-5 w-5 place-items-center border-2 ${toDevice ? "border-[#FFD028] bg-[#FFD028]" : "border-white/30"}`}>{toDevice && <Check size={13} className="text-black" />}</span><Download size={16} className="text-[#FFD028]" /><span className="flex-1 font-mono text-[12px] text-white">This device</span></button>
        <button onClick={save} disabled={busy || (!toLibrary && !toDevice)} data-testid="ws-ready-save" className="flex w-full items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] py-3 font-mono text-[12px] font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#000] disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Save reel</button>
        {onContinue && (
          <button onClick={onContinue} data-testid="ws-continue-viz"
            className="flex w-full items-center justify-center gap-2 border-2 border-black bg-[#00A6A6] py-3 font-mono text-[12px] font-black uppercase tracking-widest text-white shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Tv size={15} /> Continue to Music Viz <span aria-hidden>→</span>
          </button>
        )}
      </div>
    </div>
  );
}
